import type { AxiosError } from 'axios';
import i18n from '@/i18n';
import type { WidgetSettingsDto } from '@/api/generated';
import { getWidgetLang, pickLocalizedText } from '@/utils/localizedText';

export interface ApiErrorResponse {
  code?: string;
  message?: string;
  sala_error?: string;
}

/**
 * Extract error message from API error with custom handling for specific error codes
 */
export const getErrorMessage = (
  error: unknown,
  widgetSettings?: WidgetSettingsDto | null
): string => {
  // Default error message
  const defaultMessage = i18n.t('common.error');
  const lang = getWidgetLang(i18n.language);

  // Check if it's an Axios error
  if (isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;

    if (data) {
      // Prefer widget-settings messages when configured, otherwise fall back to i18n
      if (data.code === 'RESERVATION_MAX_PEOPLE_EXCEEDED') {
        const notice = pickLocalizedText(widgetSettings?.max_guest_notice, lang);
        return notice || i18n.t('errors.maxPeopleExceeded');
      }

      if (data.code === 'RESERVATION_FULLY_BOOKED') {
        const message = pickLocalizedText(widgetSettings?.fully_reserved_message, lang);
        return message || i18n.t('errors.fullyBooked');
      }

      if (data.code === 'STORE_CLOSED') {
        return i18n.t('errors.storeClosed');
      }

      if (data.code === 'RESERVATION_PAUSED') {
        return i18n.t('errors.reservationPaused');
      }

      if (data.code === 'STORE_ONLINE_RESERVATIONS_DISABLED') {
        return i18n.t('errors.onlineReservationsDisabled');
      }

      if (data.code === 'RESERVATION_MIN_PEOPLE_NOT_MET') {
        return i18n.t('errors.minPeopleNotMet');
      }

      // Return sala_error if available, otherwise message, otherwise default
      return data.sala_error || data.message || defaultMessage;
    }

    // Network error or no response
    if (error.message) {
      return error.message;
    }
  }

  // Generic error handling
  if (error instanceof Error) {
    return error.message;
  }

  return defaultMessage;
};

/**
 * Type guard for Axios errors
 */
function isAxiosError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (error as AxiosError).isAxiosError === true;
}

/**
 * Check if error is a specific code
 */
export const isErrorCode = (error: unknown, code: string): boolean => {
  if (isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    return data?.code === code;
  }
  return false;
};
